import { APP_PATH } from "view/routes";

export type AuthSessionEventType =
  | "login"
  | "logout"
  | "switch-start"
  | "switch-cancel";

type AuthSessionEvent = {
  id: string;
  type: AuthSessionEventType;
  originTabId: string;
  at: number;
};

type SessionSwitchState = {
  originTabId: string;
  expiresAt: number;
};

const AUTH_SESSION_EVENT_KEY = "ffair_auth_session_event";
const AUTH_SESSION_SWITCH_KEY = "ffair_auth_session_switch";
const AUTH_SESSION_TAB_ID_KEY = "ffair_auth_tab_id";
// 30s gives slow/VPN clients enough time to finish `signInWithCustomToken` +
// `getMe` + `notification.connect` before `PrivateRoute` gives up and
// redirects to LOGIN. The previous 15s window caused phantom logouts on
// degraded networks when the redirect fired before login completed.
const DEFAULT_SWITCH_TTL_MS = 30000;

const createEventId = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

export const getAuthSessionTabId = () => {
  const existingTabId = sessionStorage.getItem(AUTH_SESSION_TAB_ID_KEY);

  if (existingTabId) {
    return existingTabId;
  }

  const tabId = createEventId();
  sessionStorage.setItem(AUTH_SESSION_TAB_ID_KEY, tabId);

  return tabId;
};

const parseJson = <T>(value: string | null): T | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.warn("Failed to parse auth session sync payload", error);
    return null;
  }
};

const setStorageItem = (key: string, value: unknown) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const emitAuthSessionEvent = (type: AuthSessionEventType) => {
  setStorageItem(AUTH_SESSION_EVENT_KEY, {
    id: createEventId(),
    type,
    originTabId: getAuthSessionTabId(),
    at: Date.now(),
  } as AuthSessionEvent);
};

export const markSessionSwitchInProgress = (ttlMs = DEFAULT_SWITCH_TTL_MS) => {
  setStorageItem(AUTH_SESSION_SWITCH_KEY, {
    originTabId: getAuthSessionTabId(),
    expiresAt: Date.now() + ttlMs,
  } as SessionSwitchState);

  emitAuthSessionEvent("switch-start");
};

export const clearSessionSwitchInProgress = () => {
  localStorage.removeItem(AUTH_SESSION_SWITCH_KEY);
};

// Used when an originating tab's sign-in flow ABORTS — invalid credentials,
// cancelled OAuth callback, expired SSO link, expired one-time code, failed
// `signInWithCustomToken`. Without this signal sibling tabs that received
// the earlier `switch-start` would sit on a loader for the full 30s TTL
// even though the switch is already over. This is independent of the
// regular logout broadcast: a failed login is not a logout, and we don't
// want to log other authenticated tabs out.
export const cancelSessionSwitch = (): void => {
  if (!isSessionSwitchInProgress()) {
    return;
  }

  clearSessionSwitchInProgress();
  emitAuthSessionEvent("switch-cancel");
};

export const getSessionSwitchState = () => {
  const state = parseJson<SessionSwitchState>(
    localStorage.getItem(AUTH_SESSION_SWITCH_KEY)
  );

  if (!state) {
    return null;
  }

  if (state.expiresAt <= Date.now()) {
    localStorage.removeItem(AUTH_SESSION_SWITCH_KEY);
    return null;
  }

  return state;
};

export const isSessionSwitchInProgress = () => Boolean(getSessionSwitchState());

export const getSessionSwitchRemainingMs = () => {
  const state = getSessionSwitchState();

  if (!state) {
    return 0;
  }

  return Math.max(state.expiresAt - Date.now(), 0);
};

// In-memory only. Flips to "true" the moment a tab decides to hard-redirect
// itself to /session-changed because of a cross-tab auth event. Lives only
// until window.location.replace actually unloads the document — at that
// point the JS context resets and the flag is gone, which is exactly what
// we want: the new /session-changed document does not need it.
//
// Used by `PrivateRoute.onAuthStateChanged(null)`, `Apps.onAuthStateChanged`,
// and `firebase.tsx onUnauthorized` to suppress every "phantom logout" path
// during the tiny window between the storage event firing and the page
// finishing the hard navigation. Without this guard, Firebase's own
// IndexedDB/localStorage cross-tab sync would race our redirect and either:
//   - flip `PrivateRoute` to <Navigate to=/login>, or
//   - trigger `onUnauthorized` on a stale in-flight query, or
//   - cause `Apps.onAuthStateChanged` to dispatch resetUser/getMe on a
//     half-replaced session,
// any of which used to corrupt the originating tab's session too.
const NOTICE_REDIRECT_GRACE_MS = 10_000;
let noticeRedirectStartedAt: number | null = null;

export const beginNoticeRedirect = () => {
  noticeRedirectStartedAt = Date.now();
};

export const isNoticeRedirectInFlight = () => {
  if (noticeRedirectStartedAt == null) {
    return false;
  }

  return Date.now() - noticeRedirectStartedAt < NOTICE_REDIRECT_GRACE_MS;
};

export const redirectToAppHome = () => {
  window.location.replace(APP_PATH);
};

export const subscribeToAuthSessionEvents = (
  callback: (event: AuthSessionEvent) => void
) => {
  const currentTabId = getAuthSessionTabId();

  const handler = (storageEvent: StorageEvent) => {
    if (storageEvent.key !== AUTH_SESSION_EVENT_KEY) {
      return;
    }

    const event = parseJson<AuthSessionEvent>(storageEvent.newValue);

    if (!event || event.originTabId === currentTabId) {
      return;
    }

    callback(event);
  };

  window.addEventListener("storage", handler);

  return () => {
    window.removeEventListener("storage", handler);
  };
};
